import {
  cantidadDe,
  formatoCantidad,
  formatoFechaYHora,
  formatoHora,
  formatoPorcentaje,
  formatoPrecio,
  haceCuanto,
} from '@/lib/utils/formato-precio';
import { diaDeBolivia, sumarDias } from './rango-de-fechas';
import { MOTIVOS_DE_DESCARTE } from '../components/dialogo-descartar';
import type {
  Conteos,
  Disparo,
  EventoDeLaLinea,
  FilaDeLaCorrida,
  ResumenDelPeriodo,
  Resultado,
} from '../types/corridas.types';

/**
 * Spec 275 (R2): lo que las pantallas de las corridas dicen, sin pantalla. Los
 * textos son los de la maqueta v3 y el spec; se fijan en `corridas.test.ts`.
 */

/** «Qué la lanzó» (D-R2-2): el chip, la etiqueta corta de la tarjeta y la ayuda del «?». */
export const DISPAROS: Array<{ clave: Disparo; texto: string; corto: string; ayuda: string }> = [
  {
    clave: 'CADENCIA',
    texto: 'Calendario',
    corto: 'Calendario',
    ayuda: 'la estrategia corrió sola en su horario.',
  },
  {
    clave: 'COMPETENCIA',
    texto: 'Al llegar precios nuevos',
    corto: 'Precios nuevos',
    ayuda: 'corrió porque cambió un precio de la competencia en alguno de sus productos.',
  },
  {
    clave: 'MANUAL',
    texto: 'A mano',
    corto: 'A mano',
    ayuda: 'alguien apretó «Revisar ahora» en la estrategia; la tarjeta dice quién.',
  },
  {
    clave: 'DEVOLUCION',
    texto: 'Al cerrar otra estrategia',
    corto: 'Devolución',
    ayuda: 'corrió porque otra estrategia terminó y le devolvió productos.',
  },
  {
    clave: 'RECALCULO',
    texto: 'Recálculo de reglas',
    corto: 'Recálculo de reglas',
    ayuda:
      'alguien apretó «Aplicar las reglas y recalcular» en Reglas. Recalcula todas las estrategias a la vez y todo va a Por decidir: nada se aplica solo.',
  },
];
export const TODOS_LOS_DISPAROS: Disparo[] = DISPAROS.map((d) => d.clave);
export const cortoDelDisparo = (d: Disparo) => DISPAROS.find((x) => x.clave === d)?.corto ?? d;

/** Lo que pasó con lo propuesto, en el orden de la barra y la leyenda (maqueta). */
export const RESULTADOS: Array<{
  clave: Resultado;
  plural: string;
  singular: string;
  tono: 'ok' | 'bloqueo' | 'info' | 'alerta' | 'neutro';
  fondo: string;
}> = [
  { clave: 'APROBADA', plural: 'Aprobadas', singular: 'Aprobada', tono: 'ok', fondo: 'bg-ok' },
  {
    clave: 'DESCARTADA',
    plural: 'Descartadas',
    singular: 'Descartada',
    tono: 'bloqueo',
    fondo: 'bg-bloqueo',
  },
  {
    clave: 'SOLA',
    plural: 'Se aplicaron solas',
    singular: 'Se aplicó sola',
    tono: 'info',
    fondo: 'bg-info',
  },
  {
    clave: 'ESPERA',
    plural: 'Esperan',
    singular: 'Espera tu decisión',
    tono: 'alerta',
    fondo: 'bg-alerta',
  },
  {
    clave: 'YA_NO_APLICA',
    plural: 'Ya no aplican',
    singular: 'Ya no aplica',
    tono: 'neutro',
    fondo: 'bg-muted-foreground/50',
  },
];

export function cuantasDe(c: Conteos, r: Resultado): number {
  switch (r) {
    case 'APROBADA':
      return c.aprobadas;
    case 'DESCARTADA':
      return c.descartadas;
    case 'SOLA':
      return c.solas;
    case 'ESPERA':
      return c.esperan;
    case 'YA_NO_APLICA':
      return c.yaNoAplican;
  }
}

const DIAS_DE_LA_SEMANA = [
  'domingo',
  'lunes',
  'martes',
  'miércoles',
  'jueves',
  'viernes',
  'sábado',
];
const MESES = [
  'enero',
  'febrero',
  'marzo',
  'abril',
  'mayo',
  'junio',
  'julio',
  'agosto',
  'septiembre',
  'octubre',
  'noviembre',
  'diciembre',
];

/** «Hoy · miércoles 14 de octubre», «Ayer · martes 13 de octubre», «Lunes 12 de octubre» (de otro año, con el año). */
export function tituloDelDia(dia: string, hoy: string): string {
  const [anio, mes, d] = dia.split('-').map(Number);
  const semana = DIAS_DE_LA_SEMANA[new Date(Date.UTC(anio, mes - 1, d, 12)).getUTCDay()];
  const base = `${semana} ${d} de ${MESES[mes - 1]}${String(anio) === hoy.slice(0, 4) ? '' : ` de ${anio}`}`;
  if (dia === hoy) return `Hoy · ${base}`;
  if (dia === sumarDias(hoy, -1)) return `Ayer · ${base}`;
  return base.charAt(0).toUpperCase() + base.slice(1);
}

/** El día de Bolivia de un evento (`AAAA-MM-DD`). */
export const diaDelEvento = (e: EventoDeLaLinea) => diaDeBolivia(new Date(e.cuando));

/** «8 s», «1 min 50 s», «2 min». Sin fin: null. */
export function duracion(desde: string, hasta: string | null): string | null {
  if (!hasta) return null;
  const s = Math.max(0, Math.round((new Date(hasta).getTime() - new Date(desde).getTime()) / 1000));
  if (s < 60) return `${s} s`;
  const m = Math.floor(s / 60);
  const r = s % 60;
  return r === 0 ? `${m} min` : `${m} min ${r} s`;
}

/** Quién o qué la lanzó, para la línea gris de la tarjeta (D-R2-13: sin el nombre de la tienda). */
export function quienLaLanzo(e: EventoDeLaLinea): string {
  switch (e.disparo) {
    case 'CADENCIA':
      return 'En su horario';
    case 'COMPETENCIA':
      return 'Llegaron precios nuevos de la competencia';
    case 'MANUAL':
      return `${e.quien ?? 'Alguien'} apretó «Revisar ahora»`;
    case 'DEVOLUCION':
      return e.deEstrategia
        ? `Le devolvieron productos al cerrar «${e.deEstrategia}»`
        : 'Le devolvieron productos al cerrar otra estrategia';
    case 'RECALCULO':
      return `Lo lanzó ${e.quien ?? 'alguien'} desde Reglas`;
  }
}

/**
 * Ronda final de R4: cuántas propuso la corrida (aunque después otra corrida o
 * un recálculo las haya reescrito). El mayor entre lo que guardó al correr y lo
 * que el servidor le cuenta hoy (lo que creó más lo que reescribió): el contrato
 * de la ronda del servidor. Sin el dato (un servidor viejo o un recálculo), lo
 * que le cuenta hoy.
 */
export const cuantasPropuso = (e: EventoDeLaLinea): number =>
  Math.max(e.conteos.propuestas, e.propuestasAlCorrer ?? 0);

/**
 * Ronda del QA de R4 (R4-35): de lo que propuso, cuántas reescribió después otra
 * corrida o un recálculo: las que ya no la siguen (propuso − las que le cuenta
 * hoy) o las que el servidor cuenta como reescritas (`reescritasDespues`, que
 * incluye las que siguen ligadas a ella), la mayor. Nunca más de lo que propuso.
 */
export const cuantasReescribieron = (e: EventoDeLaLinea): number => {
  const propuso = cuantasPropuso(e);
  return Math.min(propuso, Math.max(propuso - e.conteos.propuestas, e.reescritasDespues));
};

/**
 * Ronda del QA de R4 (R4-6): la corrida que se cortó porque apagaron (o dieron de
 * baja) su estrategia mientras corría: lo que había propuesto se retiró de Por
 * decidir (el servidor lo retira en los dos casos). null en las demás (también
 * si venció: sus propuestas no se retiran).
 */
export function textoDelCorte(
  e: Pick<EventoDeLaLinea, 'cortadaPor' | 'cortadaPorApagado'>
): string | null {
  if (e.cortadaPorApagado || e.cortadaPor === 'APAGADA')
    return 'Se apagó mientras corría: lo que había propuesto se retiró de Por decidir.';
  if (e.cortadaPor === 'BAJA')
    return 'Se dio de baja mientras corría: lo que había propuesto se retiró de Por decidir.';
  return null;
}

/** «3 las reescribió…», «las 3 las reescribió…» (todas), «1 la reescribió…». */
export function reescritasEnFrase(propuso: number, reescritas: number): string {
  const cuantas =
    reescritas === propuso && propuso > 1
      ? `las ${formatoCantidad(propuso)}`
      : formatoCantidad(reescritas);
  return `${cuantas} ${reescritas === 1 ? 'la reescribió' : 'las reescribió'} después otra corrida o un recálculo`;
}

/** «—» para lo que no se sabe (nunca 0). */
export const cantidadOGuion = (n: number | null) => (n == null ? '—' : formatoCantidad(n));

/** «1 producto», «64 productos»; sin el número, «— productos» (recorrido del cierre de R2). */
export const productosOGuion = (n: number | null) =>
  n == null ? '— productos' : cantidadDe(n, 'producto', 'productos');

/**
 * Una corrida terminada que no propuso nada, no escribió nada que se haya
 * reescrito y no descartó sola ninguna vieja (D-R2-1: «sin cambios»).
 */
export const esVacia = (e: EventoDeLaLinea) =>
  e.tipo === 'CORRIDA' &&
  e.estado === 'LISTA' &&
  cuantasPropuso(e) === 0 &&
  e.reescritasDespues === 0 &&
  e.yaNoAplicaban === 0;

const claveDeEstrategia = (e: EventoDeLaLinea) => e.estrategia?.id ?? e.estrategia?.nombre ?? '';

export type ItemDeLaLinea =
  | { tipo: 'evento'; evento: EventoDeLaLinea }
  | { tipo: 'vacias'; clave: string; estrategia: string; eventos: EventoDeLaLinea[] };

export interface DiaDeLaLinea {
  dia: string;
  /** Sólo corridas de estrategias: los recálculos de reglas van aparte (D-R2-2). */
  corridas: number;
  recalculos: number;
  items: ItemDeLaLinea[];
}

/** El encabezado del día: «3 corridas», «1 recálculo», «2 corridas y 1 recálculo». */
export function textoDelDia(d: Pick<DiaDeLaLinea, 'corridas' | 'recalculos'>): string {
  const corridas = cantidadDe(d.corridas, 'corrida', 'corridas');
  const recalculos = cantidadDe(d.recalculos, 'recálculo', 'recálculos');
  if (d.recalculos === 0) return corridas;
  if (d.corridas === 0) return recalculos;
  return `${corridas} y ${recalculos}`;
}

/**
 * Por día (en el orden que vienen: de la más nueva a la más vieja) y, dentro de
 * cada día, las vacías consecutivas de la misma estrategia juntas (dos o más).
 */
export function agruparPorDia(eventos: EventoDeLaLinea[]): DiaDeLaLinea[] {
  const dias: DiaDeLaLinea[] = [];
  for (const e of eventos) {
    const dia = diaDelEvento(e);
    let d = dias[dias.length - 1];
    if (!d || d.dia !== dia) {
      d = { dia, corridas: 0, recalculos: 0, items: [] };
      dias.push(d);
    }
    if (e.tipo === 'RECALCULO') d.recalculos++;
    else d.corridas++;
    const ultimo = d.items[d.items.length - 1];
    if (esVacia(e) && ultimo) {
      if (
        ultimo.tipo === 'vacias' &&
        claveDeEstrategia(ultimo.eventos[0]) === claveDeEstrategia(e)
      ) {
        ultimo.eventos.push(e);
        continue;
      }
      if (
        ultimo.tipo === 'evento' &&
        esVacia(ultimo.evento) &&
        claveDeEstrategia(ultimo.evento) === claveDeEstrategia(e)
      ) {
        d.items[d.items.length - 1] = {
          tipo: 'vacias',
          clave: ultimo.evento.id,
          estrategia: e.estrategia?.nombre ?? '',
          eventos: [ultimo.evento, e],
        };
        continue;
      }
    }
    d.items.push({ tipo: 'evento', evento: e });
  }
  return dias;
}

/** «12 corridas sin cambios de «X» entre las 08:00 y las 20:00» (dentro de la estrategia, sin «de «X»»). */
export function textoDeVacias(
  g: Extract<ItemDeLaLinea, { tipo: 'vacias' }>,
  conNombre: boolean
): string {
  const masVieja = g.eventos[g.eventos.length - 1];
  const masNueva = g.eventos[0];
  const desde = formatoHora(masVieja.cuando);
  const hasta = formatoHora(masNueva.cuando);
  // Ronda final de R4: todas del mismo minuto, «a las 22:30».
  return `${formatoCantidad(g.eventos.length)} corridas sin cambios${
    conNombre ? ` de «${g.estrategia}»` : ''
  } ${desde === hasta ? `a las ${desde}` : `entre las ${desde} y las ${hasta}`}`;
}

/** Tasa de aprobación: aprobadas sobre aprobadas + descartadas a mano. Sin decididas: «—». */
export function tasaDeAprobacion(c: Conteos): string {
  const decididas = c.aprobadas + c.descartadasAMano;
  return decididas === 0
    ? '—'
    : formatoPorcentaje((c.aprobadas / decididas) * 100, { decimales: 0 });
}

/** Cambio promedio del propuesto sobre el precio que tenía. Sin propuestas: «—». */
export function cambioPromedio(c: Conteos): string {
  return c.conPct === 0 ? '—' : formatoPorcentaje(c.sumaPct / c.conPct, { signo: true });
}

/** Los diez números del período, en el orden de la maqueta. */
export function numerosDelPeriodo(r: ResumenDelPeriodo): Array<{
  texto: string;
  valor: string;
  resultado?: Resultado;
  ayuda?: string;
  /** Una línea chica bajo el título (los recálculos de reglas no son corridas). */
  nota?: string;
}> {
  const c = r.conteos;
  return [
    {
      texto: 'Corridas',
      valor: formatoCantidad(r.corridas),
      nota:
        r.recalculos > 0
          ? `+ ${cantidadDe(r.recalculos, 'recálculo', 'recálculos')} de reglas`
          : undefined,
    },
    { texto: 'Productos revisados', valor: formatoCantidad(r.revisados) },
    { texto: 'Propuestas', valor: formatoCantidad(c.propuestas) },
    { texto: 'Aprobadas', valor: formatoCantidad(c.aprobadas), resultado: 'APROBADA' },
    { texto: 'Descartadas', valor: formatoCantidad(c.descartadas), resultado: 'DESCARTADA' },
    { texto: 'Se aplicaron solas', valor: formatoCantidad(c.solas), resultado: 'SOLA' },
    { texto: 'Esperan', valor: formatoCantidad(c.esperan), resultado: 'ESPERA' },
    { texto: 'Ya no aplican', valor: formatoCantidad(c.yaNoAplican), resultado: 'YA_NO_APLICA' },
    {
      texto: 'Tasa de aprobación',
      valor: tasaDeAprobacion(c),
      ayuda: 'De las que se decidieron a mano: aprobadas sobre aprobadas + descartadas',
    },
    {
      texto: 'Cambio promedio',
      valor: cambioPromedio(c),
      ayuda: 'Promedio del cambio propuesto sobre el precio que tenía',
    },
  ];
}

// ── El cuadro «Productos de la corrida» ──────────────────────────────────────

export const CAMBIO = { min: -25, max: 25 } as const;
export const MARGEN = { min: 0, max: 50 } as const;
export const SIN_CATEGORIA = 'Sin categoría';
export const SIN_MARCA = 'Sin marca';
export const SIN_DATO = 'Sin dato';

export type ChipDelCuadro = 'TODAS' | Resultado | 'LIMITADAS';

export interface FiltrosDelCuadro {
  chip: ChipDelCuadro;
  q: string;
  estrategia: string;
  categoria: string;
  marca: string;
  competidor: string;
  direccion: 'TODOS' | 'BAJAN' | 'SUBEN';
  cambio: [number, number];
  margen: [number, number];
}

export const FILTROS_DEL_CUADRO: FiltrosDelCuadro = {
  chip: 'TODAS',
  q: '',
  estrategia: '',
  categoria: '',
  marca: '',
  competidor: '',
  direccion: 'TODOS',
  cambio: [CAMBIO.min, CAMBIO.max],
  margen: [MARGEN.min, MARGEN.max],
};

const sinTildes = (t: string) => t.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();

export const categoriaDe = (f: FilaDeLaCorrida) => f.categoria ?? SIN_CATEGORIA;
export const marcaDe = (f: FilaDeLaCorrida) => f.marca ?? SIN_MARCA;
export const competidorDe = (f: FilaDeLaCorrida) => f.masBarato?.tienda ?? SIN_DATO;

/**
 * ¿La fila pasa los filtros? Los extremos de las barras no ponen tope (maqueta:
 * «en −25 % entran también los que bajan más»). `ignorarChip`: para contar los chips.
 */
export function pasaElFiltro(
  f: FilaDeLaCorrida,
  x: FiltrosDelCuadro,
  ignorarChip = false
): boolean {
  if (!ignorarChip && x.chip !== 'TODAS') {
    if (x.chip === 'LIMITADAS' ? !f.limitada : f.resultado !== x.chip) return false;
  }
  const q = sinTildes(x.q.trim());
  if (q && !sinTildes(f.nombre).includes(q) && !sinTildes(f.sku).includes(q)) return false;
  if (x.estrategia && (f.estrategia ?? '') !== x.estrategia) return false;
  if (x.categoria && categoriaDe(f) !== x.categoria) return false;
  if (x.marca && marcaDe(f) !== x.marca) return false;
  if (x.competidor && competidorDe(f) !== x.competidor) return false;
  if (x.direccion === 'SUBEN' && !(f.cambio > 0.005)) return false;
  if (x.direccion === 'BAJAN' && !(f.cambio < -0.005)) return false;
  if (x.cambio[0] > CAMBIO.min && (f.cambioPct == null || f.cambioPct < x.cambio[0])) return false;
  if (x.cambio[1] < CAMBIO.max && (f.cambioPct == null || f.cambioPct > x.cambio[1])) return false;
  if (x.margen[0] > MARGEN.min && (f.margenPct == null || f.margenPct < x.margen[0])) return false;
  if (x.margen[1] < MARGEN.max && (f.margenPct == null || f.margenPct > x.margen[1])) return false;
  return true;
}

/** Cuántas hay en cada chip, con los demás filtros puestos. */
export function contarChips(
  filas: FilaDeLaCorrida[],
  x: FiltrosDelCuadro
): Record<ChipDelCuadro, number> {
  const out: Record<ChipDelCuadro, number> = {
    TODAS: 0,
    APROBADA: 0,
    DESCARTADA: 0,
    SOLA: 0,
    ESPERA: 0,
    YA_NO_APLICA: 0,
    LIMITADAS: 0,
  };
  for (const f of filas) {
    if (!pasaElFiltro(f, x, true)) continue;
    out.TODAS++;
    out[f.resultado]++;
    if (f.limitada) out.LIMITADAS++;
  }
  return out;
}

/** Las opciones de un selector del panel, sin repetir y en orden. */
export function opcionesDe(
  filas: FilaDeLaCorrida[],
  de: (f: FilaDeLaCorrida) => string | null
): string[] {
  return [...new Set(filas.map(de).filter((v): v is string => !!v))].sort((a, b) =>
    a.localeCompare(b, 'es')
  );
}

export type ColumnaDelCuadro =
  | 'nombre'
  | 'propuesto'
  | 'cambioPct'
  | 'margenPct'
  | 'masBarato'
  | 'resultado'
  | 'precioDeHoy';

export interface OrdenDelCuadro {
  columna: ColumnaDelCuadro;
  asc: boolean;
}

/** Al abrir: las que más bajan primero (la maqueta abre ordenada por el cambio). */
export const ORDEN_INICIAL: OrdenDelCuadro = { columna: 'cambioPct', asc: true };

const RANGO_DEL_RESULTADO: Record<Resultado, number> = {
  ESPERA: 0,
  APROBADA: 1,
  SOLA: 2,
  DESCARTADA: 3,
  YA_NO_APLICA: 4,
};

function valorDe(f: FilaDeLaCorrida, c: ColumnaDelCuadro): number | string | null {
  switch (c) {
    case 'nombre':
      return sinTildes(f.nombre);
    case 'propuesto':
      return f.propuesto;
    case 'cambioPct':
      return f.cambioPct;
    case 'margenPct':
      return f.margenPct;
    case 'masBarato':
      return f.masBarato?.precio ?? null;
    case 'resultado':
      return RANGO_DEL_RESULTADO[f.resultado];
    case 'precioDeHoy':
      return f.precioDeHoy;
  }
}

/** Ordena sin tocar la lista; lo que no tiene dato va al final en los dos sentidos. */
export function ordenar(filas: FilaDeLaCorrida[], o: OrdenDelCuadro): FilaDeLaCorrida[] {
  const signo = o.asc ? 1 : -1;
  return [...filas].sort((a, b) => {
    const x = valorDe(a, o.columna);
    const y = valorDe(b, o.columna);
    if (x == null && y == null) return 0;
    if (x == null) return 1;
    if (y == null) return -1;
    if (typeof x === 'string' && typeof y === 'string') return x.localeCompare(y, 'es') * signo;
    return ((x as number) - (y as number)) * signo;
  });
}

/** Tocar el título: la misma columna da vuelta el sentido; otra arranca en el suyo. */
export function cambiarOrden(o: OrdenDelCuadro, columna: ColumnaDelCuadro): OrdenDelCuadro {
  if (o.columna === columna) return { columna, asc: !o.asc };
  return { columna, asc: columna === 'nombre' || columna === 'resultado' };
}

/** El pie: la suma del cambio en Bs y el promedio del %. */
export function totales(filas: FilaDeLaCorrida[]): { suma: number; promedio: number | null } {
  const conPct = filas.filter((f) => f.cambioPct != null);
  return {
    suma: filas.reduce((s, f) => s + f.cambio, 0),
    promedio:
      conPct.length === 0
        ? null
        : conPct.reduce((s, f) => s + (f.cambioPct as number), 0) / conPct.length,
  };
}

/** El rótulo de un extremo de la barra: «menos de −25 %», «más de 25 %», «+5 %», «30 %». */
export function textoDelExtremo(
  valor: number,
  lado: 'desde' | 'hasta',
  rango: { min: number; max: number },
  conSigno: boolean
): string {
  const n = formatoPorcentaje(valor, { signo: conSigno, decimales: 0 });
  if (lado === 'desde' && valor === rango.min) return conSigno ? `menos de ${n}` : n;
  if (lado === 'hasta' && valor === rango.max) return `más de ${n}`;
  return n;
}

/** Exportada para `corridas-motivos-iguales.test.ts` (el Excel dice lo mismo). */
export const DESCARTE_SOLO: Record<string, string> = {
  VENCIO_SU_ESTRATEGIA: 'se descartó sola: venció su estrategia',
  SE_BORRO_SU_ESTRATEGIA: 'se descartó sola: se borró su estrategia',
  YA_NO_APLICA: 'se descartó sola: ya no aplicaba',
};

/**
 * El motivo elegido a mano, con el texto de la ventana Descartar (revisión T7:
 * el servidor manda la clave cruda). «Sin motivo» y una clave desconocida no
 * dicen nada: nunca se muestra la clave.
 */
const textoDelMotivo = (clave: string | null): string | null =>
  MOTIVOS_DE_DESCARTE.find((m) => m.clave === clave)?.texto ?? null;

/** El porqué, debajo de la etiqueta de «Qué pasó» (maqueta). */
export function porQueDelResultado(
  f: FilaDeLaCorrida,
  evento: Pick<EventoDeLaLinea, 'cuando'>,
  ahora: number = Date.now()
): string | null {
  switch (f.resultado) {
    case 'APROBADA':
      return f.resueltaEn
        ? `por ${f.resueltaPor ?? 'alguien'} el ${formatoFechaYHora(f.resueltaEn)}`
        : null;
    case 'DESCARTADA': {
      const solo = f.motivo ? DESCARTE_SOLO[f.motivo] : undefined;
      if (solo) return solo;
      const motivo = textoDelMotivo(f.motivo);
      if (f.resueltaPor && f.resueltaEn)
        return `por ${f.resueltaPor} el ${formatoFechaYHora(f.resueltaEn)}${motivo ? `: «${motivo}»` : ''}`;
      return motivo ? `«${motivo}»` : null;
    }
    case 'SOLA':
      return `al terminar la corrida, ${formatoFechaYHora(f.resueltaEn ?? evento.cuando)}`;
    case 'YA_NO_APLICA':
      return Math.abs(f.precioDeHoy - f.antes) > 0.005
        ? `el precio cambió a ${formatoPrecio(f.precioDeHoy)} antes de que se decidiera`
        : 'el costo cambió antes de que se decidiera';
    case 'ESPERA':
      // La fecha se renueva cuando una corrida la vuelve a proponer igual («ya esperaba»).
      return `calculada ${haceCuanto(f.calculadaEn, ahora)}`;
  }
}

/** Debajo del precio de hoy: «sin cambios» si sigue el de antes (descartadas y las que esperan). */
export const sinCambiosHoy = (f: FilaDeLaCorrida) =>
  (f.resultado === 'DESCARTADA' || f.resultado === 'ESPERA') &&
  Math.abs(f.precioDeHoy - f.antes) <= 0.005;
